import { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import type { ColDef } from 'ag-grid-community';
import { ChevronLeft, ChevronRight, Table2, X } from 'lucide-react';
import { fetchTableData } from '@/api/monitoring';
import type { TableColumn } from '@/api/monitoring';
import { useErrorToast } from '@/lib/notify';

const PAGE_SIZE = 100;

function pgTypeToFilter(dataType: string): string {
  if (['integer', 'bigint', 'smallint', 'numeric', 'real', 'double precision', 'serial', 'bigserial'].includes(dataType))
    return 'agNumberColumnFilter';
  if (['timestamp without time zone', 'timestamp with time zone', 'date'].includes(dataType))
    return 'agDateColumnFilter';
  return 'agTextColumnFilter';
}

function buildColDefs(columns: TableColumn[]): ColDef[] {
  return columns.map(col => ({
    field: col.column_name,
    headerName: col.column_name,
    filter: pgTypeToFilter(col.data_type),
    sortable: false,
    resizable: true,
    minWidth: 120,
    flex: 1,
    cellRenderer: ({ value }: { value: unknown }) => {
      if (value === null || value === undefined)
        return <span className="text-gray-300 text-xs">null</span>;
      const str = String(value);
      return (
        <span className="text-xs font-mono" title={str}>
          {str.length > 80 ? str.slice(0, 80) + '…' : str}
        </span>
      );
    },
  }));
}

interface Props {
  schema: string;
  table: string;
  onClose: () => void;
}

export default function TableDataModal({ schema, table, onClose }: Props) {
  const gridRef  = useRef<AgGridReact>(null);
  const [colDefs, setColDefs]   = useState<ColDef[]>([]);
  const [rows,    setRows]      = useState<Record<string, unknown>[]>([]);
  const [total,   setTotal]     = useState<number | null>(null);
  const [loading, setLoading]   = useState(true);
  const [page,    setPage]      = useState(1);
  const [error,   setError]     = useState<string | null>(null);
  useErrorToast(error);

  const totalPages = useMemo(() => {
    if (!total) return 1;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [total]);

  /* Sayfa değişince sadece ilgili sayfayı çek */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchTableData(schema, table, (page - 1) * PAGE_SIZE, PAGE_SIZE)
      .then(data => {
        if (cancelled) return;
        setColDefs(buildColDefs(data.columns));
        setRows(data.rows);
        setTotal(data.total);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.response?.data?.error ?? 'Veri yüklenemedi');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [schema, table, page]);

  useEffect(() => {
    setPage(1);
  }, [schema, table]);

  /* ESC ile kapat */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex flex-col bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Table2 size={18} className="text-blue-500" />
            <span className="font-semibold text-gray-800">{schema}.{table}</span>
            {total !== null && (
              <span className="ml-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-500">
                {total.toLocaleString('tr-TR')} kayıt
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 p-4">
          {loading && (
            <div className="flex h-full items-center justify-center text-gray-400 text-sm">
              Yükleniyor...
            </div>
          )}
          {!loading && !error && (
            <div className="flex h-full min-h-0 flex-col gap-3">
              <div className="ag-theme-quartz min-h-0 flex-1 rounded-xl overflow-hidden">
                <AgGridReact
                  modules={[AllEnterpriseModule]}
                  ref={gridRef}
                  columnDefs={colDefs}
                  rowData={rows}
                  rowHeight={36}
                  defaultColDef={{ resizable: true, sortable: false }}
                />
              </div>

              <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-600">
                <span>
                  {total === null ? '0' : `${((page - 1) * PAGE_SIZE) + 1}-${Math.min(page * PAGE_SIZE, total)} / ${total.toLocaleString('tr-TR')}`}
                </span>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="min-w-20 text-center text-xs font-medium text-gray-500">
                    Sayfa {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
